import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Grid, IconButton, InputAdornment, TextField, Toolbar, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { useHistory } from 'react-router';

import StyledFilledTextField from '../components/StyledFilledTextField';
import { ContextStore } from '../store/contextStore';
import { genOptions } from '../utils';
// import RemoteAutocomplete from './TripRemoteAutocomplete.js';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { DELIVERY_METHODS, EVENTS } from './utils';

const useStyles = makeStyles((theme) => ({
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
}));

// const GuestRoleUniqId = Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));

const formatListOfRoles = (roleMap) => {
    let formatted_roles = Object.keys(roleMap);

    formatted_roles = formatted_roles.map((o) => {
        return roleMap[o];
    });

    formatted_roles = [...formatted_roles, { id: null, name: 'Guest' }];
    return formatted_roles;
};

export const NotificationListToolbar = (props) => {
    const history = useHistory();
    const classes = useStyles();
    const { roleMap } = React.useContext(ContextStore);
    const { recordListFilters, setRecordListFilters } = props;

    //Query debounce
    const [searchTerm, setSearchTerm] = React.useState(props.query ? props.query : '');
    const [timeoutId, setTimeoutId] = React.useState(null);

    const list_of_role_options = formatListOfRoles(roleMap);
    const [tripQuery, setTripQuery] = React.useState(null);


    const handleAddToursFilters = (ev, tour) => {
        if (!tour) {
            setRecordListFilters((prev) => ({ ...prev, tour: null }));
            props.setRecordListPageReset(new Date().toString());
            props.setLastUpdate(new Date().toString());

            return;
        }
        setRecordListFilters((prev) => ({ ...prev, tour: tour }));
        props.setRecordListPageReset(new Date().toString());
        props.setLastUpdate(new Date().toString());
    };

    const handleTriggersSelect = (ev, nv) => {
        const next = nv == null ? [] : nv;
        setRecordListFilters((prev) => ({ ...prev, role: next }));
        props.setRecordListPageReset(new Date().toString());
        props.setLastUpdate(new Date().toString());
    };

    const handleDeliveryType = (ev, nv) => {
        if (!nv) {
            setRecordListFilters((prev) => ({ ...prev, delivery_method: null }));
            props.setRecordListPageReset(new Date().toString());
            props.setLastUpdate(new Date().toString());
            return;
        }
        setRecordListFilters((prev) => ({ ...prev, delivery_method: nv }));
        props.setRecordListPageReset(new Date().toString());
        props.setLastUpdate(new Date().toString());
    };

    const onQueryChange = (e) => {
        const searchTerm = e.target.value;
        setSearchTerm(searchTerm);
        clearTimeout(timeoutId);
        setTimeoutId(
            setTimeout(() => {
                // Call the API after the debounce timeout
                props.setQuery(searchTerm);
                history.push({ search: '' });
            }, 1000)
        );
    };

    React.useEffect(() => {
        // Cleanup function to clear the timeout on unmount and re-render
        return () => {
            clearTimeout(timeoutId);
        };
    }, [timeoutId]);

    const handleEventFilterSelect = (ev, nv) => {
        if (!nv) {
            setRecordListFilters((prev) => ({ ...prev, event: null }));
            props.setRecordListPageReset(new Date().toString());
            props.setLastUpdate(new Date().toString());
            return;
        }

        setRecordListFilters((prev) => ({ ...prev, event: nv }));
        props.setRecordListPageReset(new Date().toString());
        props.setLastUpdate(new Date().toString());
    };

    return (
        <Toolbar disableGutters={true} className={classes.toolbarRoot}>
            <Grid container direction="row" spacing={2} alignItems="center">
                <Grid item xs={12} sm={3} container spacing={2} alignItems={'center'}>
                    <Grid item xs={12}>
                        <StyledFilledTextField
                            label="Search Notifications"
                            size="small"
                            onChange={onQueryChange}
                            value={searchTerm}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Search">
                                            <IconButton aria-label="do search">
                                                <SearchIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
                {!props.isSmallScreen && (
                    <Grid item sm={props.tabFilter.name === 'tour_specific' ? 2 : 3}>
                        <Autocomplete
                            value={recordListFilters.event}
                            options={[...genOptions(EVENTS)]}
                            getOptionLabel={(option) => (option && option.name ? option.name : '')}
                            onChange={handleEventFilterSelect}
                            renderInput={(params) => <TextField {...params} variant="filled" label="Event" size="small" />}
                        />
                    </Grid>
                )}
                {!props.isSmallScreen && (
                    <Grid item sm={3}>
                        <Autocomplete
                            filterSelectedOptions
                            value={recordListFilters.role}
                            options={list_of_role_options}
                            multiple
                            isOptionEqualToValue={(option, value) =>
                                option != null &&
                                value != null &&
                                option.name === value.name &&
                                (option.id ?? null) === (value.id ?? null)
                            }
                            getOptionLabel={(option) => (option && option.name ? option.name : '')}
                            onChange={handleTriggersSelect}
                            renderInput={(params) => <TextField {...params} variant="filled" label="Triggers" size="small" />}
                        />
                    </Grid>
                )}
                {!props.isSmallScreen && (
                    <Grid item sm={2}>
                        <Autocomplete
                            value={recordListFilters.delivery_method}
                            options={[...genOptions(DELIVERY_METHODS)]}
                            getOptionLabel={(option) => (option && option.name ? option.name : '')}
                            onChange={handleDeliveryType}
                            renderInput={(params) => <TextField {...params} variant="filled" size="small" label="Delivery type" />}
                        />
                    </Grid>
                )}
                {/* The UI kinda breaks if selected multiple tours at once, will disable multiple tours until we find a solution */}
                {!props.isSmallScreen && props.tabFilter.name === 'tour_specific' && (
                    <Grid item sm={2}>
                        <RemoteAutocomplete
                            resource="tour"
                            label="Tour"
                            query={tripQuery}
                            onQueryChange={query => setTripQuery(query)}
                            onChange={handleAddToursFilters}
                            variant="filled"
                            size="small"
                            value={recordListFilters.tour}
                            getOptionLabel={(opt) => {
                                if (opt.is_bespoke) {
                                    return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                                }
                                return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                            }}
                            renderOption={(props, option) => {
                                if (option.is_bespoke) {
                                    return (
                                        <div {...props}>
                                            [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                        </div>
                                    );
                                }
                                return (
                                    <div {...props}>
                                        [id:{option.id}] {option.name} (Recurring)
                                    </div>
                                );
                            }}
                            filterOptions={(options) => options}
                        />
                    </Grid>
                )}
            </Grid>
        </Toolbar>
    );
};

export default NotificationListToolbar;
